import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { Notice } from "@mindstonehq/ui";
import { Trans } from "@lingui/react/macro";
import { Lock } from "lucide-react";
import AddToCalendar from "~/components/add-to-calendar";
import { MindspaceEventFragment } from "~/graphql/codegen/graphql";
import { isFrontend, formatDateLong } from "~/utils";
import MindspaceEventTimes from "./mindspace-event-times";
import metrics from "~/utils/metrics";

/**
 * FOX-6587: shown when the server withholds a session's link and recordings
 * (`lockedForViewer`). The one place the string lives, used by the event page
 * and the programme recap page (copy record:
 * apps/learning/docs/copy-ab/261103_cohort-session-locked-notice.md).
 */
export function CohortSessionLockedNotice({
  className,
}: {
  className?: string;
}) {
  return (
    <Notice
      tone="info"
      placement="inline"
      icon={Lock}
      className={className}
      data-testid="cohort-session-locked-notice"
    >
      <p>
        <Trans>This session is for members of its cohort.</Trans>
      </p>
    </Notice>
  );
}

export default function MindspaceEventDetails({
  event,
}: {
  event: MindspaceEventFragment;
}) {
  const frontend = isFrontend();
  if (event.lockedForViewer) {
    // No add-to-calendar on the date and no location row: the reader can
    // neither join nor watch, so the notice takes the location's place.
    return (
      <>
        <div className="flex items-center h-8 space-x-1">
          <CalendarIcon className="flex-none h-4 w-4 text-tertiary" />
          <div className="t-body-sm-medium text-muted-foreground">
            {frontend && formatDateLong(event.startDatetime)}
          </div>
        </div>
        <div className="flex items-center h-8 space-x-1">
          <ClockIcon className="flex-none h-4 w-4 text-tertiary" />
          {frontend && (
            <div className="t-body-sm-medium text-muted-foreground">
              <MindspaceEventTimes event={event} />
            </div>
          )}
        </div>
        <CohortSessionLockedNotice />
      </>
    );
  }
  return (
    <>
      <div className="flex items-center h-8 space-x-1">
        <CalendarIcon className="flex-none h-4 w-4 text-tertiary" />
        <div className="t-body-sm-medium text-muted-foreground hover:text-primary">
          <AddToCalendar
            events={[
              {
                id: event.id,
                name: event.name,
                startDatetime: event.startDatetime,
                endDatetime: event.endDatetime,
                description: event.eventDescription,
                joinUrl:
                  event.location?.__typename === "OnlineLocation"
                    ? event.location.joinUrl
                    : event.locationShort,
                publicUrl: event.publicUrl,
                agenda: event.agenda.map((x) => ({ title: x.title })),
              },
            ]}
            downloadFilename={event.name}
            track={() => {
              metrics.track("live_session_calendar", {
                page: "event",
                action: "add_event",
              });
            }}
            variant="ghost"
          >
            <div className="t-body-sm-medium text-muted-foreground">
              {frontend && formatDateLong(event.startDatetime)}
            </div>
          </AddToCalendar>
        </div>
      </div>
      <div className="flex items-center h-8 space-x-1">
        <ClockIcon className="flex-none h-4 w-4 text-tertiary" />
        {frontend && (
          <div className="t-body-sm-medium text-muted-foreground">
            <MindspaceEventTimes event={event} />
          </div>
        )}
      </div>
      <div
        className="flex items-center h-8 space-x-1"
        data-testid="mindspace-event-location"
      >
        <MapPinIcon className="flex-none h-4 w-4 text-tertiary" />
        {event.location?.__typename === "GeoLocation" && (
          <a
            target="_blank"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              event.location.address,
            )}`}
            className="t-body-sm-medium text-muted-foreground hover:text-primary"
          >
            {event.location.address}
          </a>
        )}
        {event.location?.__typename === "OnlineLocation" && (
          <a
            target="_blank"
            href={event.location.joinUrl}
            className="t-body-sm-medium text-muted-foreground hover:text-primary"
          >
            {event.location.text}
          </a>
        )}
      </div>
    </>
  );
}
